import { formatDate } from "@angular/common";
import { inject, Injectable, signal } from "@angular/core";
import { SigningStatus } from "@models/annual-statements/annual-statement-enums";
import { DocumentRecord } from "@models/annual-statements/document-record";
import { AsicStatus } from "@models/documents/asic-status.enum";
import { DocumentStatusEnum } from "@models/documents/document-status.enum";
import { CellDataType } from "@models/grid/cell-data-type.enum";
import { FilterOption } from "@models/grid/grid-filter";
import { PageResult } from "@models/requests/page-result.interface";
import {
  AgAsicDocumentStatusComponent
} from "@shared/components/grid/ag-cells/common/ag-asic-document-status/ag-asic-document-status.component";
import {
  AgCompanyDebtComponent
} from "@shared/components/grid/ag-cells/common/ag-company-debt/ag-company-debt.component";
import { AgDateCellComponent } from "@shared/components/grid/ag-cells/common/ag-date-cell/ag-date-cell.component";
import {
  AgDocumentSigningStatusComponent
} from "@shared/components/grid/ag-cells/common/ag-document-signing-status/ag-document-signing-status.component";
import {
  AgDocumentStatusComponent
} from "@shared/components/grid/ag-cells/common/ag-document-status/ag-document-status.component";
import {
  AgReminderTagComponent
} from "@shared/components/grid/ag-cells/common/ag-remider-tag/ag-reminder-tag.component";
import {
  AgTextWithLinkComponent
} from "@shared/components/grid/ag-cells/common/ag-text-with-link/ag-text-with-link.component";
import {
  AgDocumentNameComponent
} from "@shared/components/grid/ag-cells/documents/ag-document-name/ag-document-name.component";
import { excludedDocumentTypes, formTypesList } from "@shared/constants/forms";
import { DocumentTypePipe } from "@shared/pipes/document-type.pipe";
import { BaseGridService } from "@shared/services/base/base-grid.service";
import { DocumentsApiService } from "@shared/services/documents-api.service";
import { convertEnumToFilterOption } from "@shared/utils/enum-to-filter-option.helper";
import { dateComparator } from "@shared/utils/grid/date-comparator";
import {
  getGenerateDocumentInvoicePath,
  getGenerateDocumentPath,
  getImportedDocumentPagePath
} from "@shared/utils/route-path-utils";
import { uniqueArrayToFilterOptionHelper } from "@shared/utils/unique-array-to-filter-option.helper";
import {
  ColDef,
  ICellRendererParams,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  IServerSideGetRowsRequest,
  ValueFormatterParams
} from "ag-grid-community";
import { Subject } from "rxjs";

@Injectable()
export class DocumentsGridService extends BaseGridService {
  private documentsApiService = inject(DocumentsApiService);

  entityId = signal<string | null>(null);
  totalRecords = signal<number>(0);
  dataSource = signal<IServerSideDatasource>(this.getDataSource());

  dataLoaded$ = new Subject<PageResult<DocumentRecord>>();

  // filters
  documentCanBeOpened = false;
  profileUserNameList: string[] = [];
  accountManagerFilterOptions: FilterOption[] = [];
  documentStatusFilterOptions: FilterOption[] = convertEnumToFilterOption(DocumentStatusEnum);
  signingStatusFilterOptions: FilterOption[] = convertEnumToFilterOption(SigningStatus);
  documentAsicStatusFilterOptions: FilterOption[] = convertEnumToFilterOption(AsicStatus);
  documentSigningStatusFilterOptions: FilterOption[] = convertEnumToFilterOption(SigningStatus);
  documentFormTypeFilterOptions: FilterOption[] = uniqueArrayToFilterOptionHelper(formTypesList).map(o => {
    o.label = o.label.replace('C:', '');
    return o;
  });

  setGridFilterOptions(
    documentCanBeOpened: boolean,
    profileUserNameList: string[],
  ): void {
    this.documentCanBeOpened = documentCanBeOpened;
    this.profileUserNameList = profileUserNameList;
    this.accountManagerFilterOptions = uniqueArrayToFilterOptionHelper(profileUserNameList);
  }

  setDataSource(searchText?: string, endRow?: number): void {
    this.dataSource.set(this.getDataSource(searchText, endRow));
  }

  getDataSource(searchText?: string, endRow?: number): IServerSideDatasource {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        const filterModel = { ...(params.request.filterModel ?? {}) };

        if (!filterModel.type) {
          filterModel.type = {
            filterType: 'exclude',
            values: excludedDocumentTypes
          };
        }

        if (this.entityId()) {
          filterModel['entityId'] = {
            filterType: 'set',
            values: [this.entityId()]
          };
        }

        const request: IServerSideGetRowsRequest = {
          ...params.request,
          filterModel
        };

        if (endRow) {
          request.endRow = endRow;
        }

        this.documentsApiService.getDocumentList(request, searchText)
          .subscribe({
            next: (result) => {
              this.totalRecords.set(result.total);

              params.success({
                rowData: result.records,
                rowCount: result.total,
              });

              this.dataLoaded$.next(result);
            },
            error: (err) => {
              console.error(err);
              params.fail();
            },
          });
      },
    };
  }

  setDocumentsColDefs(actionColumn: ColDef): void {
    const colDefs: ColDef[] = [
      {
        headerName: 'Company Name',
        field: 'documentName',
        suppressMovable: true,
        flex: 2,
        cellDataType: CellDataType.TEXT,
        cellRenderer: AgDocumentNameComponent,
        cellClass: 'h-100 d-flex align-items-center',
        cellRendererParams: { documentCanBeOpened: this.documentCanBeOpened },
        menuTabs: [],
        filterParams: {}
      },
      {
        headerName: 'Form',
        field: 'type',
        flex: 1,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.documentFormTypeFilterOptions,
          values: formTypesList
        },
        valueFormatter: (params: ValueFormatterParams<DocumentRecord, string>) => DocumentTypePipe.formatValue(params.data?.type),
        menuTabs: []
      },
      {
        headerName: 'Description',
        field: 'description',
        flex: 2,
        menuTabs: []
      },
      {
        headerName: 'Document Status',
        field: 'status',
        flex: 1,
        cellRenderer: AgDocumentStatusComponent,
        cellClass: 'd-flex align-items-center',
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.documentStatusFilterOptions,
          values: Object.values(DocumentStatusEnum).filter((v) => !isNaN(Number(v))),
        },
        menuTabs: []
      },
      {
        headerName: 'Signing Status',
        field: 'signingStatus',
        flex: 1,
        cellRenderer: AgDocumentSigningStatusComponent,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.signingStatusFilterOptions,
          values: Object.values(SigningStatus).filter((v) => !isNaN(Number(v))),
        },
        menuTabs: []
      },
      {
        headerName: 'ASIC Status',
        field: 'asicStatus',
        flex: 1,
        cellRenderer: AgAsicDocumentStatusComponent,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.documentAsicStatusFilterOptions,
          values: Object.values(AsicStatus).filter((v) => !isNaN(Number(v))),
        },
        menuTabs: []
      },
      {
        headerName: 'Late Fee',
        field: 'lateFee',
        flex: 1,
        sortable: false,
        filter: 'agNumberColumnFilter',
        cellRenderer: AgCompanyDebtComponent,
        cellDataType: CellDataType.NUMBER,
        menuTabs: [],
      },
      {
        headerName: 'Late Fee',
        field: 'hasLateFee',
        suppressColumnsToolPanel: true,
        lockVisible: true,
        sortable: false,
        hide: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: [
            { label: 'Yes', value: 'true', active: false },
            { label: 'No', value: 'false', active: false }
          ],
          values: [true, false]
        },
        cellRendererParams: { allowEdit: false },
        menuTabs: [],
      },
      {
        headerName: 'Date Signed',
        field: 'dateSigned',
        hide: true,
        flex: 1,
        filterParams: {
          comparator: dateComparator,
        },
        valueFormatter: (params: ValueFormatterParams<DocumentRecord, Date>) => {
          return params.value ? formatDate(params.value ?? '', 'dd MMM yyyy', 'en-US') : '';
        },
        cellDataType: CellDataType.DATE,
        menuTabs: []
      },
      {
        headerName: 'Lodgement Deadline',
        field: 'lodgementDeadline',
        flex: 1,
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: dateComparator,
        },
        valueFormatter: (params: ValueFormatterParams<DocumentRecord, Date>) => {
          return params.value ? formatDate(params.value ?? '', 'dd MMM yyyy', 'en-US') : '';
        },
        cellDataType: CellDataType.DATE,
        menuTabs: []
      },
      {
        headerName: 'Lodgement Date',
        field: 'lodgedOn',
        flex: 1,
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: dateComparator,
        },
        valueFormatter: (params: ValueFormatterParams<DocumentRecord, Date>) => {
          return params.value ? formatDate(params.value ?? '', 'dd MMM yyyy', 'en-US') : '';
        },
        cellDataType: CellDataType.DATE,
        menuTabs: []
      },
      {
        headerName: 'Account Manager',
        field: 'accountManagerFullName',
        flex: 0.5,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.accountManagerFilterOptions,
          values: this.profileUserNameList,
        },
        hide: true,
        menuTabs: [],
      },
      {
        headerName: 'Date Created',
        field: 'createdOn',
        flex: 1,
        filter: 'agDateColumnFilter',
        hide: true,
        filterParams: {
          comparator: dateComparator,
        },
        valueFormatter: (params: ValueFormatterParams<DocumentRecord, Date>) => {
          return formatDate(params.value ?? '', 'dd MMM yyyy', 'en-US');
        },
        cellDataType: CellDataType.DATE,
        menuTabs: []
      },
      {
        headerName: 'Date Modified',
        field: 'modifiedOn',
        hide: true,
        flex: 1,
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: dateComparator,
        },
        valueFormatter: (params: ValueFormatterParams<DocumentRecord, Date>) => {
          return formatDate(params.value ?? '', 'dd MMM yyyy', 'en-US');
        },
        cellDataType: CellDataType.DATE,
        menuTabs: []
      },
      {
        headerName: 'Signing Reminders',
        field: 'signingReminders',
        flex: 0.5,
        menuTabs: [],
        sortable: false,
        cellRenderer: AgReminderTagComponent,
        hide: true
      },
      {
        headerName: 'Last Editor',
        field: 'lastEditorFullName',
        flex: 0.5,
        hide: true,
        menuTabs: [],
      },
      actionColumn
    ]
      .map((colDef) => ({ ...colDef, suppressHeaderMenuButton: true } as ColDef));

    this.colDefs.set(colDefs);
  }

  setEntityDocumentsColDefs(actionColumn: ColDef, entityId: string): void {
    const colDefs: ColDef[] = [
      {
        headerName: 'Description',
        field: 'description',
        width: 300,
        flex: 2,
        cellRenderer: AgTextWithLinkComponent,
        cellRendererParams: {
          getLinkFn: (params: ICellRendererParams<DocumentRecord, string>) => {
            const record = params.data as DocumentRecord;

            if (!record.documentId) return null;
            if (DocumentRecord.isImportedDocument(record)) return getImportedDocumentPagePath(record.documentId);
            if (DocumentRecord.isDocumentInvoice(record)) return getGenerateDocumentInvoicePath(record.documentId);

            return getGenerateDocumentPath(record.documentId);
          }
        }
      },
      {
        headerName: 'Form',
        field: 'type',
        flex: 1,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.documentFormTypeFilterOptions,
          values: formTypesList
        },
        valueFormatter: (params: ValueFormatterParams<DocumentRecord, string>) => DocumentTypePipe.formatValue(params.data?.type),
      },
      {
        headerName: 'Document Status',
        field: 'status',
        filter: 'agSetColumnFilter',
        width: 165,
        minWidth: 165,
        filterParams: {
          filterOptions: this.documentStatusFilterOptions,
          values: Object.values(DocumentStatusEnum).filter((v) => !isNaN(Number(v))),
        },
        cellRenderer: AgDocumentStatusComponent,
        cellClass: 'd-flex align-items-center',
      },
      {
        headerName: 'ASIC Status',
        field: 'asicStatus',
        filter: 'agSetColumnFilter',
        minWidth: 90,
        flex: 1,
        filterParams: {
          filterOptions: this.documentAsicStatusFilterOptions,
          values: Object.values(AsicStatus).filter((v) => !isNaN(Number(v))),
        },
        cellRenderer: AgAsicDocumentStatusComponent
      },
      {
        headerName: 'Date Created',
        field: 'createdOn',
        filter: 'agDateColumnFilter',
        width: 110,
        cellRenderer: AgDateCellComponent,
        filterParams: {
          comparator: dateComparator,
        },
        valueFormatter: (params: ValueFormatterParams<DocumentRecord, Date>) => {
          return formatDate(params.value ?? '', 'dd MMM yyyy', 'en-US');
        },
        cellDataType: CellDataType.DATE,
      },
      {
        headerName: 'Late Fee',
        field: 'lateFee',
        filter: 'agNumberColumnFilter',
        flex: 1,
        sortable: false,
        cellRenderer: AgCompanyDebtComponent,
        cellDataType: CellDataType.NUMBER,
        hide: true,
      },
      {
        headerName: 'Late Fee',
        field: 'hasLateFee',
        suppressColumnsToolPanel: true,
        lockVisible: true,
        sortable: false,
        hide: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: [
            { label: 'Yes', value: 'true', active: false },
            { label: 'No', value: 'false', active: false }
          ],
          values: [true, false]
        },
        cellRendererParams: { allowEdit: false },
        menuTabs: [],
      },
      {
        headerName: 'Signing Status',
        field: 'signingStatus',
        filter: 'agSetColumnFilter',
        flex: 1,
        cellRenderer: AgDocumentSigningStatusComponent,
        menuTabs: [],
        filterParams: {
          filterOptions: this.signingStatusFilterOptions,
          values: Object.values(SigningStatus).filter((v) => !isNaN(Number(v))),
        },
        hide: true,
      },
      {
        headerName: 'Date Signed',
        field: 'dateSigned',
        flex: 1,
        cellRenderer: AgDateCellComponent,
        filterParams: {
          comparator: dateComparator,
        },
        cellDataType: CellDataType.DATE,
        hide: true,
      },
      {
        headerName: 'Lodgement Deadline',
        field: 'lodgementDeadline',
        filter: 'agDateColumnFilter',
        flex: 1,
        cellRenderer: AgDateCellComponent,
        filterParams: {
          comparator: dateComparator,
        },
        valueFormatter: (params: ValueFormatterParams<DocumentRecord, Date>) => {
          return params.value ? formatDate(params.value ?? '', 'dd MMM yyyy', 'en-US') : '';
        },
        cellDataType: CellDataType.DATE,
        hide: true,
      },
      {
        headerName: 'Lodgement Date',
        field: 'lodgedOn',
        filter: 'agDateColumnFilter',
        flex: 1,
        cellRenderer: AgDateCellComponent,
        filterParams: {
          comparator: dateComparator,
        },
        valueFormatter: (params: ValueFormatterParams<DocumentRecord, Date>) => {
          return params.value ? formatDate(params.value ?? '', 'dd MMM yyyy', 'en-US') : '';
        },
        cellDataType: CellDataType.DATE,
        hide: true,
      },
      {
        headerName: 'Date Modified',
        field: 'modifiedOn',
        filter: 'agDateColumnFilter',
        flex: 1,
        cellRenderer: AgDateCellComponent,
        filterParams: {
          comparator: dateComparator,
        },
        valueFormatter: (params: ValueFormatterParams<DocumentRecord, Date>) => {
          return formatDate(params.value ?? '', 'dd MMM yyyy', 'en-US');
        },
        cellDataType: CellDataType.DATE,
        hide: true,
      },
      {
        headerName: 'Last Editor',
        field: 'lastEditorFullName',
        flex: 0.5,
        hide: true,
      },
      actionColumn,
    ]
      .map((colDef) => ({ ...colDef, suppressHeaderMenuButton: true, }));

    this.colDefs.set(colDefs);
    this.entityId.set(entityId);
    this.setDataSource();
  }
}
